// Smart Daara - Virtual school: "Mes notes" (automatic transcript from learning activity)
import React, { useEffect, useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { reportsAPI, downloadBlob } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { FileDown } from 'lucide-react';
import { toast } from 'sonner';

const mentionColor = (m) => {
  if (!m) return 'bg-slate-100 text-slate-500';
  if (['Excellent'].includes(m)) return 'bg-emerald-100 text-emerald-700';
  if (['Très bien', 'Very good'].includes(m)) return 'bg-teal-100 text-teal-700';
  if (['Bien', 'Good'].includes(m)) return 'bg-sky-100 text-sky-700';
  if (['Passable', 'Pass'].includes(m)) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
};

export default function MyGrades({ studentId }) {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    reportsAPI.transcript(studentId, language).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [studentId, language]);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await reportsAPI.transcriptPdf(studentId, language);
      downloadBlob(res.data, `bulletin_${(data?.student_name || 'eleve').replace(/ /g, '_')}.pdf`);
    } catch (e) { toast.error(fr ? 'Téléchargement impossible' : 'Download failed'); }
    finally { setDownloading(false); }
  };

  if (loading) return <div className="flex justify-center p-12"><div className="animate-spin w-8 h-8 border-2 border-emerald-600 border-t-transparent rounded-full" /></div>;
  if (!data) return <p className="text-slate-400 text-sm text-center py-8">{fr ? 'Aucune donnée disponible' : 'No data available'}</p>;

  return (
    <div className="space-y-5" data-testid="my-grades-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-slate-900">{fr ? 'Mes notes' : 'My grades'}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {data.is_final
              ? (fr ? 'Bulletin final — programme terminé' : 'Final transcript — program completed')
              : (fr ? `Bulletin provisoire — situation au ${data.as_of}` : `Provisional transcript — as of ${data.as_of}`)}
          </p>
        </div>
        <Button className="bg-emerald-700" disabled={downloading} onClick={download} data-testid="download-transcript-btn">
          <FileDown className="w-4 h-4 mr-2" />
          {fr ? 'Télécharger mon bulletin' : 'Download my transcript'}
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-700">{data.global_progress}%</p>
          <p className="text-xs text-slate-500">{fr ? 'Suivi global des cours' : 'Global course progress'}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
          <p className="text-2xl font-bold text-emerald-700">{data.global_success != null ? `${data.global_success}%` : '—'}</p>
          <p className="text-xs text-slate-500">{fr ? 'Réussite moyenne aux devoirs' : 'Avg assignment success'}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-4 text-center">
          <Badge className={`${mentionColor(data.global_mention)} text-sm`}>{data.global_mention || '—'}</Badge>
          <p className="text-xs text-slate-500 mt-2">{fr ? 'Mention globale' : 'Global honor'}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-sm overflow-x-auto">
        <CardContent className="p-0">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="bg-slate-50 text-left text-slate-500">
                <th className="p-3">{fr ? 'Matière' : 'Subject'}</th>
                <th className="p-2">{fr ? 'Progression' : 'Progress'}</th>
                <th className="p-2">{fr ? 'Devoirs faits' : 'Assignments done'}</th>
                <th className="p-2">{fr ? 'Réussite' : 'Success'}</th>
                <th className="p-2">{fr ? 'Validés (≥80%)' : 'Validated (≥80%)'}</th>
                <th className="p-2">Mention</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.subject_id} className="border-t border-slate-100" data-testid={`grades-row-${r.subject_id}`}>
                  <td className="p-3 font-medium">{r.subject}</td>
                  <td className="p-2 min-w-[140px]">
                    <div className="flex items-center gap-2">
                      <Progress value={r.progress} className="h-2 flex-1" />
                      <span className="text-xs text-slate-500 w-10">{r.progress}%</span>
                    </div>
                  </td>
                  <td className="p-2">{r.quizzes_done} / {r.quizzes_total}</td>
                  <td className="p-2">{r.success_rate != null ? `${r.success_rate}%` : '—'}</td>
                  <td className="p-2">{r.validated}</td>
                  <td className="p-2"><Badge className={`${mentionColor(r.mention)} text-xs`}>{r.mention || '—'}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.rows.length === 0 && <p className="text-slate-400 text-sm text-center py-6">{fr ? 'Aucune matière avec contenu' : 'No subjects with content'}</p>}
        </CardContent>
      </Card>
    </div>
  );
}
